import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import authService from '../services/authService';

/**
 * OnboardingPage (3-step wizard with Prev/Next)
 *
 * Flows supported:
 * - Login (email or Google, existing user): skip wizard and go Home immediately.
 * - Register (email): Key already chosen at register form. Step 1 just confirms key. Then optional Instagram. Then Finish → Home.
 * - Register (Google): Show Step 1 asking for comercio_key, complete registration with the pending credential, then Step 2 Instagram (optional), then Finish → Home.
 *
 * State flags in localStorage:
 * - onboarding_mode: 'register_google' | 'register_email' | 'login'
 * - onboarding_done: 'true' | 'false'
 * - onboarding_step: '1' | '2' | '3' (persists current step)
 * - google_pending_credential: string (stored when backend returns 409 for Google Sign-In)
 *
 * Also accepts OAuth fallback query params (?jwt,&comercio_key,&timezone_iana) and persists them if present.
 */
const OnboardingPage: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user);
  const navigate = useNavigate();

  // Accept jwt/comercio_key/timezone_iana from OAuth fallback redirect (if used)
  React.useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const jwt = sp.get('jwt');
      const ck = sp.get('comercio_key');
      const tz = sp.get('timezone_iana');

      let changed = false;
      if (jwt && jwt.trim()) {
        localStorage.setItem('jwtToken', jwt);
        changed = true;
      }
      if (ck && ck.trim()) {
        localStorage.setItem('comercio_key', ck);
        // keep alias in sync
        localStorage.setItem('comercioKey', ck);
        changed = true;
      }
      if (tz && tz.trim()) {
        localStorage.setItem('timezone_iana', tz);
        changed = true;
      }
      if (changed) {
        localStorage.setItem('onboarding_done', 'false');
        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState({}, '', url.toString());
      }
    } catch { /* Best-effort cleanup. */ }
  }, []);

  const mode = React.useMemo<'register_google' | 'register_email' | 'login'>(() => {
    const m = (typeof localStorage !== 'undefined' ? localStorage.getItem('onboarding_mode') : null) || '';
    if (m === 'register_google' || m === 'register_email' || m === 'login') return m as any;
    return 'login';
  }, []);

  // Skip immediately for login flow or if onboarding already completed
  React.useEffect(() => {
    const done = (typeof window !== 'undefined' && window.localStorage)
      ? (localStorage.getItem('onboarding_done') === 'true')
      : true;
    if (user && (mode === 'login' || done)) {
      navigate('/', { replace: true });
    }
  }, [mode, navigate, user]);

  // Wizard step state with persistence
  const [step, setStep] = React.useState<number>(() => {
    const val = typeof localStorage !== 'undefined' ? Number(localStorage.getItem('onboarding_step') || '1') : 1;
    return Math.min(Math.max(val || 1, 1), 3);
  });
  React.useEffect(() => {
    try { localStorage.setItem('onboarding_step', String(step)); } catch { /* Best-effort cleanup. */ }
  }, [step]);

  // comercio_key input (for Google register path)
  const [comercioKeyInput, setComercioKeyInput] = React.useState<string>(() => {
    try {
      const existing = localStorage.getItem('comercio_key') || '';
      return existing || '';
    } catch {
      return '';
    }
  });

  // Helpers
  const normalizeKey = (text: string): string =>
    String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 50);

  const canGoPrev = step > 1;

  const canGoNext = React.useMemo(() => {
    if (step === 1) {
      if (mode === 'register_google') {
        return normalizeKey(comercioKeyInput).length >= 3;
      }
      return true; // register_email just confirms
    }
    if (step === 2) return true; // optional IG
    if (step === 3) return true; // finish handled by finish()
    return true;
  }, [step, mode, comercioKeyInput]);

  const prev = () => {
    if (canGoPrev) setStep((s) => Math.max(1, s - 1));
  };

  // Step 1 "Next" behavior varies by mode
  const handleNextFromStep1 = async () => {
    if (mode === 'register_google') {
      const key = normalizeKey(comercioKeyInput);
      if (!key || key.length < 3) return;
      // Complete Google registration with pending credential
      const cred = localStorage.getItem('google_pending_credential') || '';
      const tz = localStorage.getItem('timezone_iana') || 'Europe/Madrid';
      try {
        await authService.loginWithGoogle({
          credential: cred,
          comercio_key: key,
          timezone_iana: tz,
        });
        localStorage.setItem('comercio_key', key);
        try { localStorage.removeItem('google_pending_credential'); } catch { /* Best-effort cleanup. */ }
      } catch {
        alert('Failed to complete Google registration. Please try again.');
        return;
      }
    } else {
      // register_email: ensure canonical key is persisted
      const existing = localStorage.getItem('comercio_key') || '';
      if (existing) localStorage.setItem('comercio_key', existing);
    }
    setStep(2);
  };

  const next = async () => {
    if (step === 1) {
      await handleNextFromStep1();
      return;
    }
    if (step === 2) {
      // Instagram optional; proceed to Finish
      setStep(3);
      return;
    }
  };

  const finish = React.useCallback(async () => {
    try {
      await authService.completeOnboarding();
    } catch {
      // Keep onboarding incomplete on the server so the next login can resume safely.
      alert('No se pudo guardar la finalización. Inténtalo de nuevo.');
      return;
    }
    try {
      localStorage.setItem('onboarding_done', 'true');
      try { localStorage.removeItem('onboarding_mode'); } catch { /* Best-effort cleanup. */ }
      try { localStorage.removeItem('onboarding_step'); } catch { /* Best-effort cleanup. */ }
      try { localStorage.removeItem('google_pending_credential'); } catch { /* Best-effort cleanup. */ }
      try { window.dispatchEvent(new Event('auth:login')); } catch { /* Best-effort cleanup. */ }
    } catch { /* Best-effort cleanup. */ }
    navigate('/', { replace: true });
  }, [navigate]);

  // Derived display values
  const detectedComercioKey = React.useMemo(() => {
    const key = (typeof window !== 'undefined' && window.localStorage)
      ? (localStorage.getItem('comercio_key') || user?.comercioKey || '')
      : (user?.comercioKey || '');
    return key || '';
  }, [user]);

  // Step cards
  const renderStep1 = () => {
    if (mode === 'register_google') {
      return (
        <div className="rounded-lg border border-white/20 p-4">
          <h2 className="text-lg font-semibold text-white/80">Step 1 of 3 — Choose your store key</h2>
          <p className="text-white/60 mt-1">
            Pick a unique comercio_key for your store. You can use lowercase letters, numbers, and underscores.
          </p>
          <input
            type="text"
            value={comercioKeyInput}
            onChange={(e) => setComercioKeyInput(e.target.value)}
            onBlur={(e) => setComercioKeyInput(normalizeKey(e.target.value))}
            placeholder="e.g. my_store_123"
            className="mt-3 block w-full px-4 py-2.5 border border-white/20 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="text-xs text-white/50 mt-2">
            Minimum 3 characters. Only a-z, 0-9 and underscores are allowed.
          </p>
        </div>
      );
    }

    // register_email: confirm existing key
    return (
      <div className="rounded-lg border border-white/20 p-4">
        <h2 className="text-lg font-semibold text-white/80">Step 1 of 3 — Confirm your store key</h2>
        <p className="text-white/60 mt-1">
          Your comercio_key (nickname) is:
          <span className="ml-2 font-mono px-2 py-1 bg-white/10 rounded border border-white/20">
            {detectedComercioKey || '—'}
          </span>
        </p>
        <p className="text-xs text-white/50 mt-2">
          This identifies your tenant and is used in URLs and schema names.
        </p>
      </div>
    );
  };

  const renderStep2 = () => {
    return (
      <div className="rounded-lg border border-white/20 p-4">
        <h2 className="text-lg font-semibold text-white/80">Step 2 of 3 — Connect Instagram (optional)</h2>
        <p className="text-white/60 mt-1">
          You can connect Instagram later. Without it, chat features still work for other channels.
        </p>
        <div className="mt-3 flex gap-3">
          <Link
            to="/connections"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-blue-600 text-blue-700 hover:bg-blue-50"
          >
            Go to Connections
          </Link>
          <a
            href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-white/20 text-white/70 hover:bg-black/10"
          >
            Meta Developer Console
          </a>
        </div>
        <p className="text-xs text-white/50 mt-3">
          You can skip this step and connect Instagram whenever you want from the Connections page.
        </p>
      </div>
    );
  };

  const renderStep3 = () => {
    return (
      <div className="rounded-lg border border-white/20 p-4">
        <h2 className="text-lg font-semibold text-white/80">Step 3 of 3 — Finish</h2>
        <p className="text-white/60 mt-1">Your workspace is ready. You can start using your dashboard now.</p>
        <button
          onClick={finish}
          className="mt-3 inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
        >
          Go to Home
        </button>
      </div>
    );
  };

  const renderBody = () => {
    if (mode === 'login') {
      // Safety: if we still reach here due to timing, show minimal card and redirect shortly
      return (
        <div className="rounded-lg border border-white/20 p-4">
          <h2 className="text-lg font-semibold text-white/80">Redirecting…</h2>
          <p className="text-white/60 mt-1">You are already signed in. Taking you to Home.</p>
        </div>
      );
    }
    if (step === 1) return renderStep1();
    if (step === 2) return renderStep2();
    return renderStep3();
  };

  return (
    <div className="min-h-screen bg-black/10 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full bg-black/20 shadow-xl rounded-xl border border-white/20 p-6 md:p-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-extrabold text-white/80">Welcome</h1>
          <p className="text-white/60 mt-1">Complete these quick steps to finish your setup.</p>
        </div>

        {/* Progress indicator */}
        {mode !== 'login' && (
          <div className="mb-4 flex items-center gap-2 text-sm text-white/60">
            <span className={`px-2 py-1 rounded ${step >= 1 ? 'bg-indigo-100 text-indigo-700' : 'bg-white/10'}`}>1</span>
            <span>→</span>
            <span className={`px-2 py-1 rounded ${step >= 2 ? 'bg-indigo-100 text-indigo-700' : 'bg-white/10'}`}>2</span>
            <span>→</span>
            <span className={`px-2 py-1 rounded ${step >= 3 ? 'bg-indigo-100 text-indigo-700' : 'bg-white/10'}`}>3</span>
          </div>
        )}

        {renderBody()}

        {/* Prev/Next controls */}
        {mode !== 'login' && step < 3 && (
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={prev}
              disabled={!canGoPrev}
              className={`inline-flex items-center px-4 py-2 rounded-lg border ${canGoPrev ? 'border-white/20 text-white/70 hover:bg-black/10' : 'border-white/20 text-white/40 cursor-not-allowed'}`}
            >
              Previous
            </button>
            <button
              onClick={next}
              disabled={!canGoNext}
              className={`inline-flex items-center px-5 py-2.5 rounded-lg ${canGoNext ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-indigo-300 text-white cursor-not-allowed'}`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
